import { hashSeed, mulberry32 } from '../math/rng.js';

/**
 * @file DESTRUCTIBLE STREET FURNITURE. Drive into a streetlight and it goes
 * over; drive into a bench and it comes apart into three pieces that tumble
 * across the pavement and stay where they land.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT IS AN INSTANCE MATRIX, NOT A PHYSICS BODY
 * ─────────────────────────────────────────────────────────────────────────────
 * Every breakable prop in the level is already ONE INSTANCE of an existing
 * `InstancedBatch` (`levels/city/props.js`), and breaking one is a write to its
 * matrix — the same one draw call, before and after, for the whole city. There
 * is no new mesh, no new material, no per-wreck geometry and nothing to free.
 * A bench's three planks are pre-allocated at BUILD time, parked at zero scale
 * inside the bench they belong to; the break reveals them. That is why the
 * batch caps are `benches` and `benches * 3` and not a pool: a pool would have
 * to decide which wreck to reclaim, and a city that tidies up behind the player
 * is worse than one that does not.
 *
 * ⚠ `InstancedBatch#setMatrix` is the ONLY legal way in. It moves the
 * authoritative matrix, the instance's cull sphere and the draw slot together;
 * writing `_mat` would leave a fallen lamp being culled against the volume it
 * occupied while it was standing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO three.js — THE VIEW IS INJECTED
 * ─────────────────────────────────────────────────────────────────────────────
 * Like `traffic/Traffic.js` and `systems/Driving.js`, this file is pure: it
 * owns the state machine and the arcs, and hands finished POSES to a `view`
 * (`levels/shared/breakables.js#createBreakableView`) that does the matrix
 * composition. `view: null` is a legal, fully-simulating world, which is what
 * lets `tests/breakables.test.mjs` prove the fall, the scatter and the
 * determinism in node.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT BREAKS THINGS
 * ─────────────────────────────────────────────────────────────────────────────
 * A CAR, and only a car. The player's driven body and the driverless one it
 * left coasting both count; AI traffic does not, and cannot — an AI car is an
 * arc length on a lane (see `Traffic.js`) and a lane never passes within reach
 * of a kerb prop, so testing them would be 20 distance checks a frame for an
 * event that can never fire. Gunfire does not break furniture either: the
 * hitscan resolves against ACTORS and there is no world ray to hang it off,
 * so a bench that shattered when you shot at a pedestrian standing behind it
 * would be lying about what happened. {@link Breakables.smashAt} is the door
 * for whoever adds one.
 *
 * The car pays for it: {@link BREAK.carDrag} of its speed per prop taken out,
 * applied by the caller through `onSmash` (this file never touches a car body —
 * it is handed positions, not systems).
 *
 * @module engine/systems/Breakables
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * A breakable, as the LEVEL publishes it. `handle` and `debris` are logical
 * instance indices into the batches the view knows about — see
 * `levels/city/props.js`, which is the only thing that builds these.
 *
 * @typedef {Object} BreakableProp
 * @property {'lamp'|'bench'} kind Which batch, and which failure it has.
 * @property {number} x @property {number} y @property {number} z Base, world.
 * @property {number} yaw The yaw it was placed at — a fallen lamp keeps it, so
 *   its arm does not swing round to face the way the car came from.
 * @property {number} r Hit radius, metres.
 * @property {number} handle Logical instance index of the prop itself.
 * @property {number[]} [debris] Logical indices of its pre-allocated pieces.
 */

/**
 * The pose a view is asked to draw. ONE object, reused for every write — this
 * runs per active prop per frame and must not litter.
 *
 * `tilt` is a rotation about the HORIZONTAL axis perpendicular to
 * `(tiltX, tiltZ)`, applied in WORLD space on top of `yaw`, so `tilt = π/2`
 * lays a standing prop flat pointing along `(tiltX, tiltZ)`. Expressing the
 * topple as an axis-angle rather than as Euler pitch/roll is what keeps the
 * lamp's own yaw independent of the direction it falls in.
 *
 * @typedef {Object} BreakPose
 * @property {number} x @property {number} y @property {number} z
 * @property {number} yaw @property {number} tilt
 * @property {number} tiltX @property {number} tiltZ Unit, horizontal.
 * @property {number} scale 0 hides the instance outright.
 */

/**
 * Tuning. Metres, seconds, radians.
 */
export const BREAK = Object.freeze({
  /**
   * Below this closing speed a car pushes past a prop without taking it out.
   * It is deliberately just over `DRIVE.exitSpeed` (3): kerb-crawling into a
   * bench while parking must not demolish it, and anything you would describe
   * as "driving at" it is well past this.
   */
  carSpeed: 3.4,
  /** Half-width of the car used for the overlap test — a bumper, not a body. */
  carRadius: 1.1,
  /** Fraction of the car's speed each prop it destroys takes with it. */
  carDrag: 0.13,

  /** Seconds a streetlight takes to go from upright to flat. */
  fallTime: 0.9,
  /**
   * ...and the settle after it lands. The pole rebounds a few degrees and
   * dies; without it a 8 m steel column stops dead at exactly horizontal,
   * which is the one frame that gives away that this is a matrix and not a
   * body.
   */
  settleTime: 0.45,
  /** Rebound angle at the moment of impact, radians, decaying over the settle. */
  bounce: 0.09,
  /** ...and how many rebounds fit into the settle. */
  bounceCycles: 1.6,

  /** Pieces a bench comes apart into. Matches the batch cap the level sizes. */
  pieces: 3,
  /** Ballistic gravity for a piece, m/s². Heavier than 9.81 — a short, blunt
   *  arc reads as debris, a long floaty one reads as a ragdoll. */
  gravity: 15,
  /** Launch speed along the impact direction, before the per-piece spread. */
  pieceSpeed: 4.2,
  /** ...and the sideways spread, ± this many m/s. */
  pieceSpread: 1.9,
  /** Upward kick, m/s. */
  pieceLift: 3.2,
  /** Tumble rate in flight, rad/s. */
  pieceSpin: 7,
  /** Metres a piece starts above the base of the bench it came out of. */
  pieceY: 0.45,
  /** How long a landed piece takes to flop flat — up to half a turn, so it is
   *  a beat rather than a snap. See the settle in `stepBench`. */
  pieceSettle: 0.3,
});

/** @enum {number} */
const ST = Object.freeze({ STANDING: 0, FALLING: 1, DOWN: 2 });

const HALF_PI = Math.PI / 2;

/**
 * @param {Object} o
 * @param {BreakableProp[]} [o.props] The level's list. Taken by reference and
 *   never mutated — the state lives in this system's own arrays, so a level
 *   rebuild is a new system and there is nothing to reset.
 * @param {{setPose: (kind: string, handle: number, pose: BreakPose) => void}|null} [o.view]
 *   `levels/shared/breakables.js#createBreakableView`, or null to simulate
 *   headless.
 * @param {(x: number, z: number) => number} [o.heightAt]
 * @param {any} [o.bus] Emits `'prop:broken'` `{kind, x, z}` once per prop.
 * @param {((p: BreakableProp, by: any) => void)|null} [o.onSmash] Called on the
 *   step a prop is destroyed, with whatever hit it — this is where the CALLER
 *   slows that car down. Never called for a prop that is already down.
 * @param {string|number} [o.seed='breakables'] Seeds the debris scatter. Same
 *   seed, same wreck; it draws from its OWN per-prop stream and never touches
 *   a shared one (rule 4).
 * @returns {Object}
 */
export function createBreakables(o = {}) {
  const {
    props = [],
    view = null,
    heightAt = () => 0,
    bus = null,
    onSmash = null,
    seed = 'breakables',
  } = o;

  const n = props.length;
  const state = new Uint8Array(n);
  /** Seconds since this prop was hit. */
  const clock = new Float32Array(n);
  /** Impact direction, unit and horizontal. */
  const dirX = new Float32Array(n);
  const dirZ = new Float32Array(n);
  /**
   * Per-PIECE flight state, `pieces` entries per prop. Flat arrays rather than
   * objects for the same reason `EnemyManager` is: this is walked every frame
   * for every wreck on the island and a wreck is forever.
   */
  const P = BREAK.pieces;
  const pX = new Float32Array(n * P);
  const pY = new Float32Array(n * P);
  const pZ = new Float32Array(n * P);
  const pVX = new Float32Array(n * P);
  const pVY = new Float32Array(n * P);
  const pVZ = new Float32Array(n * P);
  const pYaw = new Float32Array(n * P);
  const pTilt = new Float32Array(n * P);
  const pSpin = new Float32Array(n * P);
  /** Ground under each piece, sampled ONCE at launch. */
  const pGround = new Float32Array(n * P);
  /** Seconds since this piece landed; −1 while it is still in the air. */
  const pRest = new Float32Array(n * P);
  /** Tilt the piece is settling TOWARD — the nearest flat lie. */
  const pFlat = new Float32Array(n * P);

  /** Props still animating. A wreck that has settled costs nothing. */
  const active = new Set();
  /** @type {BreakPose} ONE object for every write — see the typedef. */
  const pose = { x: 0, y: 0, z: 0, yaw: 0, tilt: 0, tiltX: 1, tiltZ: 0, scale: 1 };
  const root = hashSeed(String(seed)) >>> 0;
  let broken = 0;
  let disposed = false;

  /**
   * Hands {@link pose} to the view. A handle of −1 is a prop the level could
   * not fit inside its batch cap: it simulates, it just does not draw.
   * @param {string} kind @param {number} handle
   */
  const write = (kind, handle) => {
    if (view && handle >= 0) view.setPose(kind, handle, pose);
  };

  // ── smashing ───────────────────────────────────────────────────────────────

  /**
   * Destroys prop `i`, thrown in the direction `(dx, dz)`.
   *
   * @param {number} i
   * @param {number} dx @param {number} dz Impact direction; normalised here,
   *   and a zero vector falls back to the prop's own facing so a prop can never
   *   fall in the direction `(0, 0)`.
   * @param {any} [by] Whatever hit it, passed straight through to `onSmash`.
   *   For the car path that is the body itself, so the caller can slow down THE
   *   ONE THAT HIT IT — the player can be driving a second car while the one
   *   they bailed out of is still ploughing through a bus stop.
   * @returns {boolean} False when there was nothing left to break.
   */
  const smash = (i, dx, dz, by = null) => {
    if (i < 0 || i >= n || state[i] !== ST.STANDING) return false;
    const p = props[i];
    const len = Math.hypot(dx, dz);
    const fx = len > 1e-4 ? dx / len : Math.sin(p.yaw);
    const fz = len > 1e-4 ? dz / len : Math.cos(p.yaw);
    state[i] = ST.FALLING;
    clock[i] = 0;
    dirX[i] = fx;
    dirZ[i] = fz;
    broken++;
    active.add(i);

    if (p.kind === 'bench') launchPieces(i, fx, fz);
    onSmash?.(p, by);
    bus?.emit?.('prop:broken', { kind: p.kind, x: p.x, z: p.z });
    return true;
  };

  /**
   * The bench's pieces, thrown out from under the bumper.
   *
   * The rng is seeded from the PROP's identity, not from a running stream, so
   * the same bench always comes apart the same way whatever order the player
   * wrecks the city in — the same rule `Traffic` uses for its bays, and for the
   * same reason.
   *
   * @param {number} i @param {number} fx @param {number} fz
   */
  const launchPieces = (i, fx, fz) => {
    const p = props[i];
    const r = mulberry32((root + hashSeed(`${i}:${Math.round(p.x)}:${Math.round(p.z)}`)) >>> 0);
    // The bench is REPLACED by its pieces on the step it breaks, not on the
    // first animated one: a bench sitting inside its own wreckage for a frame
    // is the tell. A hidden instance never needs touching again.
    pose.x = p.x;
    pose.y = p.y;
    pose.z = p.z;
    pose.yaw = p.yaw;
    pose.tilt = 0;
    pose.tiltX = 1;
    pose.tiltZ = 0;
    pose.scale = 0;
    write('bench', p.handle);
    // Right of the impact direction — the axis the spread is measured along.
    const rx = -fz;
    const rz = fx;
    for (let k = 0; k < P; k++) {
      const s = i * P + k;
      // Spread the pieces across the bench's own length before they move, so
      // three planks do not start life inside each other.
      const along = (k - (P - 1) / 2) * 0.55;
      pX[s] = p.x + Math.sin(p.yaw) * along;
      pZ[s] = p.z + Math.cos(p.yaw) * along;
      pY[s] = p.y + BREAK.pieceY;
      pGround[s] = heightAt(pX[s], pZ[s]);
      const spread = (r() * 2 - 1) * BREAK.pieceSpread;
      const push = BREAK.pieceSpeed * (0.55 + r() * 0.75);
      pVX[s] = fx * push + rx * spread;
      pVZ[s] = fz * push + rz * spread;
      pVY[s] = BREAK.pieceLift * (0.6 + r() * 0.8);
      pYaw[s] = Math.atan2(pVX[s], pVZ[s]);
      pTilt[s] = 0;
      pSpin[s] = BREAK.pieceSpin * (0.5 + r()) * (r() < 0.5 ? -1 : 1);
      pRest[s] = -1;
      pFlat[s] = 0;
    }
  };

  /**
   * Breaks everything standing within `r` of a point. The door for anything
   * that is not a car — an explosion, a world-ray shot — and the reason
   * {@link smash} takes an index rather than a position.
   *
   * @param {number} x @param {number} z @param {number} r
   * @param {number} [dx] @param {number} [dz] Direction; defaults to outward
   *   from the point, which is what an explosion wants.
   * @returns {number} Props destroyed.
   */
  const smashAt = (x, z, r, dx, dz) => {
    let hit = 0;
    for (let i = 0; i < n; i++) {
      if (state[i] !== ST.STANDING) continue;
      const p = props[i];
      const ex = p.x - x;
      const ez = p.z - z;
      const reach = r + p.r;
      if (ex * ex + ez * ez > reach * reach) continue;
      if (smash(i, dx ?? ex, dz ?? ez)) hit++;
    }
    return hit;
  };

  // ── the step ───────────────────────────────────────────────────────────────

  /**
   * @param {number} i @param {number} dt
   */
  const stepLamp = (i, dt) => {
    const p = props[i];
    const t = clock[i];
    let tilt;
    if (t < BREAK.fallTime) {
      // A toppling pole ACCELERATES — it is a pendulum about its base, not a
      // lerp. Squaring the parameter is the cheapest curve that starts at rest
      // and arrives fast, which is the only part of it anyone reads.
      const u = t / BREAK.fallTime;
      tilt = HALF_PI * u * u;
    } else {
      const u = Math.min(1, (t - BREAK.fallTime) / BREAK.settleTime);
      // Rebound: damped bounces AWAY from the ground and back. `abs` is
      // load-bearing — a plain sine goes negative past one half-cycle and
      // rotates the pole PAST flat, i.e. through the tarmac, which at
      // `bounceCycles` 1.6 is most of the settle.
      tilt = HALF_PI - BREAK.bounce * Math.abs(Math.sin(u * Math.PI * BREAK.bounceCycles)) * (1 - u);
      if (u >= 1) {
        tilt = HALF_PI;
        state[i] = ST.DOWN;
        active.delete(i);
      }
    }
    pose.x = p.x;
    pose.y = p.y;
    pose.z = p.z;
    pose.yaw = p.yaw;
    pose.tilt = tilt;
    pose.tiltX = dirX[i];
    pose.tiltZ = dirZ[i];
    pose.scale = 1;
    write('lamp', p.handle);
    void dt;
  };

  /**
   * @param {number} i @param {number} dt
   */
  const stepBench = (i, dt) => {
    const p = props[i];
    const ids = p.debris ?? [];
    let moving = 0;
    for (let k = 0; k < P; k++) {
      const s = i * P + k;
      if (pRest[s] < 0) {
        pVY[s] -= BREAK.gravity * dt;
        pX[s] += pVX[s] * dt;
        pY[s] += pVY[s] * dt;
        pZ[s] += pVZ[s] * dt;
        pTilt[s] += pSpin[s] * dt;
        if (pY[s] <= pGround[s]) {
          pY[s] = pGround[s];
          pRest[s] = 0;
          // ⚠ THE NEAREST MULTIPLE OF **2** PI, not of PI. A slat looks
          // symmetric, so `PI` is the tempting answer and it is wrong: the
          // prototype is baked in isolation with its top face open to the sky
          // and its underside against the ground, so a piece that settles at
          // an odd multiple lands showing a face that was baked BLACK. Every
          // second plank came out looking like a scorch mark on the pavement.
          // Snapping to the even multiple costs at most half a turn during the
          // settle, and that reads as the piece flopping over as it stops.
          pFlat[s] = Math.round(pTilt[s] / (2 * Math.PI)) * 2 * Math.PI;
        }
        moving++;
      } else if (pRest[s] < BREAK.pieceSettle) {
        pRest[s] += dt;
        const u = Math.min(1, pRest[s] / BREAK.pieceSettle);
        pTilt[s] += (pFlat[s] - pTilt[s]) * u;
        if (u >= 1) {
          pTilt[s] = pFlat[s];
          pRest[s] = BREAK.pieceSettle;
        } else {
          moving++;
        }
      }
      pose.x = pX[s];
      pose.y = pY[s];
      pose.z = pZ[s];
      pose.yaw = pYaw[s];
      pose.tilt = pTilt[s];
      // The piece tumbles about the axis across its own flight, which is what
      // makes it go end over end rather than cartwheel.
      pose.tiltX = Math.sin(pYaw[s]);
      pose.tiltZ = Math.cos(pYaw[s]);
      pose.scale = 1;
      write('debris', ids[k] ?? -1);
    }
    if (moving === 0) {
      state[i] = ST.DOWN;
      active.delete(i);
    }
  };

  /**
   * One fixed step.
   *
   * @param {number} dt
   * @param {Array<{x: number, z: number, vx: number, vz: number, speed: number}>|null} [cars]
   *   The player's driven body and, while it rolls, the one they bailed out of.
   *   Null skips the hit test entirely, which is what a paused or headless
   *   caller wants.
   */
  const update = (dt, cars = null) => {
    if (disposed || !(dt > 0)) return;

    if (cars) {
      for (let c = 0; c < cars.length; c++) {
        const car = cars[c];
        if (!car || Math.abs(car.speed) < BREAK.carSpeed) continue;
        const v = Math.hypot(car.vx, car.vz);
        // A car reversing into a bench still breaks it, and the prop still
        // falls the way the car is GOING — which is backwards. The velocity
        // vector already says that; `speed` (signed along the heading) only
        // decides whether it was fast enough to count.
        const fx = v > 1e-4 ? car.vx / v : 0;
        const fz = v > 1e-4 ? car.vz / v : 0;
        for (let i = 0; i < n; i++) {
          if (state[i] !== ST.STANDING) continue;
          const p = props[i];
          const ex = p.x - car.x;
          const ez = p.z - car.z;
          const reach = BREAK.carRadius + p.r;
          if (ex * ex + ez * ez > reach * reach) continue;
          smash(i, fx, fz, car);
        }
      }
    }

    if (!active.size) return;
    // The live set, deliberately, and NOT the snapshot `Missions#clearActors`
    // takes: the hazard there is an ARRAY spliced under a `for…of`, which skips
    // entries. `stepLamp`/`stepBench` only ever `delete` the entry currently
    // being visited, which a `Set` iterator is specified to handle, and nothing
    // adds during the walk — the hit test above has already finished.
    for (const i of active) {
      clock[i] += dt;
      if (props[i].kind === 'lamp') stepLamp(i, dt);
      else stepBench(i, dt);
    }
  };

  return {
    name: 'breakables',
    /** @returns {number} Props in the level, standing or not. */
    get count() {
      return n;
    },
    smash,
    smashAt,
    update,
    /** @param {number} i @returns {boolean} */
    isStanding: (i) => state[i] === ST.STANDING,
    /**
     * @returns {{props: number, broken: number, active: number, lamps: number,
     *   benches: number}}
     */
    stats() {
      let lamps = 0;
      let benches = 0;
      for (let i = 0; i < n; i++) {
        if (state[i] === ST.STANDING) continue;
        if (props[i].kind === 'lamp') lamps++;
        else benches++;
      }
      return { props: n, broken, active: active.size, lamps, benches };
    },
    dispose() {
      disposed = true;
      active.clear();
    },
  };
}
